/**
 * LiveSlots - Client-side resolution for live cart variables.
 *
 * The server resolves 28/35 template variables at init time.
 * LiveSlots handles the remaining 4 cart variables that change
 * when visitors add/remove items mid-session.
 */

export interface LiveSlotDef {
  template: string;
  slots: Record<string, {
    source: 'cart';
    field: string;
    format?: 'currency' | 'number';
  }>;
}

export interface LiveCart {
  total: number;
  itemCount: number;
  lastItemName: string | null;
  currency: string;
}

export class LiveSlots {
  private currency: string;
  private locale: string;

  constructor(config: { currency: string; locale: string }) {
    this.currency = config.currency;
    this.locale = config.locale;
  }

  /**
   * Resolve live slots in all instructions using current cart data.
   * Mutates instruction nodes in place for performance.
   */
  resolve(instructions: Record<string, any>, cart: LiveCart): void {
    for (const instruction of Object.values(instructions)) {
      if (!instruction?.liveSlots) continue;

      for (const [path, slotDef] of Object.entries(instruction.liveSlots as Record<string, LiveSlotDef>)) {
        let result = slotDef.template;

        for (const [key, def] of Object.entries(slotDef.slots)) {
          const raw = cart[def.field as keyof LiveCart];
          const formatted = this.format(raw, def.format);
          result = result.replaceAll(`{{${key}}}`, formatted);
        }

        this.setByPath(instruction.node || instruction.render?.message, path, result);
      }
    }
  }

  private format(value: unknown, format?: string): string {
    if (format === 'currency') {
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
        maximumFractionDigits: 0,
      }).format(Number(value));
    }
    if (format === 'number') {
      return new Intl.NumberFormat(this.locale).format(Number(value));
    }
    return String(value ?? '');
  }

  /**
   * Set a value at a dot/bracket path in an object.
   * Handles: "sections[0].content", "message.title", etc.
   */
  private setByPath(obj: any, path: string, value: string): void {
    if (!obj || !path) return;

    const parts = path.match(/[^.\[\]]+/g);
    if (!parts || parts.length === 0) return;

    let current = obj;
    for (let i = 0; i < parts.length - 1; i++) {
      const key = /^\d+$/.test(parts[i]) ? Number(parts[i]) : parts[i];
      current = current?.[key];
      if (current === undefined || current === null) return;
    }

    const lastKey = /^\d+$/.test(parts[parts.length - 1])
      ? Number(parts[parts.length - 1])
      : parts[parts.length - 1];
    current[lastKey] = value;
  }
}
